import { useCallback, useEffect, useRef, useState } from "react";

import { newrelic } from "@jti/fe-clients";
import type {
  SecuredFieldsCreditCardDetails,
  SecuredFieldsFieldErrorType,
  SecuredFieldsSessionInstance,
  SecuredFieldsValidationResult,
} from "@jti/fe-common";

import { getSecuredFieldsSessionConstructor, usePaymentProviderContext } from "@checkout-v2/features/payment";

type SecuredFieldsSessionState =
  | { status: "idle" }
  | { status: "initializing" }
  | { status: "loading-sdk" }
  | { status: "configuring" }
  | { status: "ready" }
  | { status: "submitting" }
  | { status: "validated"; sessionId: string; cardDetails?: SecuredFieldsCreditCardDetails }
  | { status: "field-errors"; errors: Record<string, SecuredFieldsFieldErrorType> }
  | { status: "error"; message: string }
  | { status: "general-validation-error"; message: string };

type UseSecuredFieldsSessionParams = {
  enabled: boolean;
  paymentMethodCode: string;
  fieldSelectors: Record<string, string>;
  locale?: string;
};

type UseSecuredFieldsSessionResult = {
  sessionState: SecuredFieldsSessionState;
  validateCard: () => void;
  resetErrors: () => void;
};

const toError = (error: unknown, fallbackMessage: string): Error =>
  error instanceof Error ? error : new Error(fallbackMessage);

const destroySecuredFields = (session: SecuredFieldsSessionInstance | null, paymentMethodCode: string): void => {
  try {
    session?.destroy();
  } catch (error) {
    newrelic.noticeError(toError(error, "Failed to destroy secured fields"), {
      context: "useSecuredFieldsSession - secured fields cleanup",
      paymentMethodCode,
    });
  }
};

export const useSecuredFieldsSession = ({
  enabled,
  paymentMethodCode,
  fieldSelectors,
  locale,
}: UseSecuredFieldsSessionParams): UseSecuredFieldsSessionResult => {
  const [sessionState, setSessionState] = useState<SecuredFieldsSessionState>({ status: "idle" });
  const { createPaymentSession } = usePaymentProviderContext();

  const sessionRef = useRef<SecuredFieldsSessionInstance | null>(null);
  const fieldSelectorsRef = useRef(fieldSelectors);
  fieldSelectorsRef.current = fieldSelectors;
  const localeRef = useRef(locale);
  localeRef.current = locale;

  const reportError = useCallback(
    (error: unknown, context: string, fallbackMessage: string) => {
      const normalizedError = toError(error, fallbackMessage);
      newrelic.noticeError(normalizedError, {
        context: `useSecuredFieldsSession - ${context}`,
        paymentMethodCode,
      });
      setSessionState({ status: "error", message: normalizedError.message });
    },
    [paymentMethodCode]
  );

  // reports general error, not tied to a specific field
  const reportValidationError = useCallback(
    (error: unknown, context: string, fallbackMessage: string) => {
      const normalizedError = toError(error, fallbackMessage);
      newrelic.noticeError(normalizedError, {
        context: `useSecuredFieldsSession - ${context}`,
        paymentMethodCode,
      });
      setSessionState({ status: "general-validation-error", message: normalizedError.message });
    },
    [paymentMethodCode]
  );

  useEffect(() => {
    if (!enabled) {
      setSessionState({ status: "idle" });
      return;
    }

    // Flips to true once this effect run is superseded (disable, method change, unmount).
    let isStale = false;

    const handleInitialized = (status: "ok" | "error", message?: string): void => {
      if (isStale) return;

      if (status === "ok") {
        setSessionState({ status: "ready" });
        return;
      }

      reportError(
        new Error(message ?? "Failed to initialize payment fields"),
        "secured fields initialization",
        "Failed to initialize payment fields"
      );
    };

    const handleFormUpdate = (result: SecuredFieldsValidationResult): void => {
      if (isStale) return;

      switch (result.status) {
        case "ok":
          setSessionState({
            status: "validated",
            sessionId: result.sessionId,
            cardDetails: result.cardDetails,
          });
          break;
        case "fields_in_error":
          setSessionState({ status: "field-errors", errors: result.errors });
          break;
        case "error":
          reportValidationError(
            new Error(result.message),
            "secured fields form update",
            "Payment fields validation failed"
          );
          break;
      }
    };

    const startSession = async (): Promise<void> => {
      try {
        setSessionState({ status: "initializing" });

        const { session_id, merchant } = await createPaymentSession(paymentMethodCode);

        if (isStale) return;

        const SessionConstructor = getSecuredFieldsSessionConstructor();

        if (!SessionConstructor) {
          throw new Error("Secured fields session constructor is not available");
        }

        const session = new SessionConstructor({ merchantId: merchant });
        sessionRef.current = session;

        setSessionState({ status: "loading-sdk" });
        await session.loadSdk();
        if (isStale) return;

        setSessionState({ status: "configuring" });

        session.configure({
          session: session_id,
          fields: fieldSelectorsRef.current,
          locale: localeRef.current,
          onInitialized: handleInitialized,
          onFormUpdate: handleFormUpdate,
        });
      } catch (error) {
        if (isStale) return;
        reportError(error, "session initialization", "Session initialization failed");
      }
    };

    startSession();

    return () => {
      isStale = true;
      destroySecuredFields(sessionRef.current, paymentMethodCode);
      sessionRef.current = null;
    };
  }, [enabled, paymentMethodCode, createPaymentSession, reportError, reportValidationError]);

  const validateCard = useCallback(() => {
    if (
      sessionState.status !== "ready" &&
      sessionState.status !== "field-errors" &&
      sessionState.status !== "general-validation-error"
    ) {
      return;
    }

    try {
      const session = sessionRef.current;
      if (!session) return;

      setSessionState({ status: "submitting" });
      session.validate();
    } catch (error) {
      reportValidationError(error, "card validation", "Payment fields validation failed");
    }
  }, [sessionState.status, reportValidationError]);

  const resetErrors = useCallback(() => {
    setSessionState((prev) =>
      prev.status === "field-errors" || prev.status === "general-validation-error" ? { status: "ready" } : prev
    );
  }, []);

  return { sessionState, validateCard, resetErrors };
};
